import React from "react";
import {
  Form,
  Input,
  Radio,
  Row,
  Col,
  Button,
  Space,
  Tag,
  Tooltip,
  Flex,
  Typography,
} from "antd";
import {
  MinusCircleOutlined,
  PlusOutlined,
  CheckCircleOutlined,
} from "@ant-design/icons";

const { Text } = Typography;

/**
 * 项目表单中的通用组件
 */

// 带图标的部分标题
export const SectionTitle = ({
  icon,
  title,
  subtitle,
  color = "#1890ff",
  tooltip,
}: {
  icon: string;
  title: string;
  subtitle?: string;
  color?: string;
  tooltip?: string;
}) => (
  <div
    style={{
      fontSize: 16,
      fontWeight: 600,
      marginBottom: 16,
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      color: "#262626",
      borderBottom: "1px solid #f0f0f0",
      paddingBottom: 12,
    }}
  >
    <div style={{ display: "flex", alignItems: "center" }}>
      <i
        className={icon}
        style={{ marginRight: 8, color: color, fontSize: 18 }}
      />
      {title}
      {subtitle && (
        <Text
          type="secondary"
          style={{ fontSize: 13, fontWeight: "normal", marginLeft: 8 }}
        >
          {subtitle}
        </Text>
      )}
    </div>
    {tooltip && (
      <Tooltip title={tooltip}>
        <i
          className="ri-question-line"
          style={{ color: "#8c8c8c", fontSize: 14, cursor: "help" }}
        />
      </Tooltip>
    )}
  </div>
);

// 项目名称表单项
export const ProjectNameField = () => (
  <Form.Item
    name="projectName"
    label="项目名称"
    rules={[{ required: true, message: "请输入项目名称" }]}
  >
    <Input
      placeholder="请输入项目名称"
      prefix={<i className="ri-file-list-line" style={{ color: "#bfbfbf" }} />}
      maxLength={10}
      showCount
      size="large"
      autoComplete="off"
    />
  </Form.Item>
);

// 摘要语言选择表单项
export const SummaryLanguageField = () => (
  <Form.Item
    name="summaryLanguage"
    label={
      <div style={{ display: "flex", alignItems: "center" }}>
        输出的摘要语言
        <Tooltip title="选择多国语言将支持生成其他语言的摘要，但会消耗更多Token">
          <i
            className="ri-question-line"
            style={{ marginLeft: 8, color: "#8c8c8c", cursor: "help" }}
          />
        </Tooltip>
      </div>
    }
    initialValue="中文"
  >
    <Radio.Group buttonStyle="solid" style={{ width: "100%" }}>
      <Row gutter={16}>
        <Col span={12}>
          <Radio.Button
            value="中文"
            style={{
              width: "100%",
              height: "42px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <i
              className="ri-translate"
              style={{ marginRight: 8, fontSize: 16 }}
            />
            仅中文摘要
          </Radio.Button>
        </Col>
        <Col span={12}>
          <Radio.Button
            value="多国语言"
            style={{
              width: "100%",
              height: "42px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <i
              className="ri-global-line"
              style={{ marginRight: 8, fontSize: 16 }}
            />
            根据网页 lang 属性适配
          </Radio.Button>
        </Col>
      </Row>
    </Radio.Group>
  </Form.Item>
);

// 网站绑定表单列表
export const WebsiteBindingField = ({
  noSites = "未添加任何网站将允许所有网站的请求，可能导致API密钥被滥用",
  subtitle = "(限制项目允许来自哪些站点的API请求)",
  title = "绑定的网站",
}: {
  noSites?: string;
  subtitle?: string;
  title?: string;
}) => (
  <div
    style={{
      backgroundColor: "#f9f0ff",
      padding: "16px 20px",
      borderRadius: 8,
      marginBottom: 20,
      border: "1px solid #d3adf7",
    }}
  >
    <div
      style={{
        display: "flex",
        alignItems: "center",
        marginBottom: 16,
        fontWeight: 500,
        color: "#531dab",
        borderBottom: "1px dashed #d3adf7",
        paddingBottom: 10,
      }}
    >
      <i className="ri-global-line" style={{ marginRight: 8, fontSize: 16 }} />
      {title}
      <span
        style={{
          fontSize: 13,
          fontWeight: "normal",
          marginLeft: 8,
          color: "#8c8c8c",
        }}
      >
        {subtitle}
      </span>
    </div>

    <Form.List name="websites" initialValue={[""]}>
      {(fields, { add, remove }) => (
        <>
          {fields.map(({ key, name, ...restField }) => (
            <Flex
              key={key}
              style={{
                display: "flex",
                marginBottom: 12,
                width: "100%",
              }}
              align="baseline"
            >
              <Form.Item
                {...restField}
                name={name}
                rules={[
                  { type: "url", message: "请输入有效的URL" },
                  {
                    pattern: /^https:\/\/.+/,
                    message: "因为安全考虑，使用公开请求只允许HTTPS网址添加",
                  },
                ]}
                style={{ margin: 0, width: "100%" }}
              >
                <Input
                  placeholder="请输入网站URL（例如：https://example.com）"
                  prefix={
                    <i className="ri-link" style={{ color: "#722ed1" }} />
                  }
                  size="middle"
                  allowClear
                />
              </Form.Item>
              {fields.length > 1 && (
                <Button
                  type="text"
                  danger
                  icon={<MinusCircleOutlined />}
                  onClick={() => remove(name)}
                  style={{
                    borderRadius: "50%",
                    minWidth: "32px",
                    height: "32px",
                  }}
                />
              )}
            </Flex>
          ))}
          <Form.Item style={{ marginBottom: 0, marginTop: 12 }}>
            <Button
              type="dashed"
              onClick={() => add()}
              block
              icon={<PlusOutlined />}
              disabled={fields.length >= 10}
              style={{ height: "40px" }}
            >
              添加网站（最多10个）
            </Button>
            {fields.length === 0 && (
              <div
                style={{
                  marginTop: 8,
                  color: "#8c8c8c",
                  fontSize: 13,
                  background: "#f5f5f5",
                  padding: "8px 12px",
                  borderRadius: 4,
                }}
              >
                <i
                  className="ri-error-warning-line"
                  style={{ marginRight: 4 }}
                />
                {noSites}
              </div>
            )}
          </Form.Item>
        </>
      )}
    </Form.List>
  </div>
);

// 项目类型卡片
export const ProjectTypeCard = ({
  type,
  isSelected,
  title,
  icon,
  color,
  features,
  note,
  disabled = false,
  onClick,
}: {
  type: string;
  isSelected: boolean;
  title: string;
  icon: string;
  color: string;
  features: string[];
  note: string;
  disabled?: boolean;
  onClick: () => void;
}) => (
  <div
    style={{
      border: `1px solid ${isSelected ? color : "#e8e8e8"}`,
      borderRadius: 8,
      padding: "16px",
      background: isSelected ? `${color}10` : "#fff",
      cursor: disabled ? "not-allowed" : "pointer",
      height: "100%",
      boxShadow: isSelected ? `0 2px 8px rgba(0, 0, 0, 0.04)` : "none",
      transition: "all 0.3s",
      opacity: disabled ? 0.6 : 1,
      position: "relative",
    }}
    onClick={() => {
      if (!disabled) {
        onClick();
      }
    }}
  >
    {type === "postchat" && (
      <div
        style={{
          position: "absolute",
          top: 0,
          right: 0,
          background: "#1890ff",
          color: "#fff",
          padding: "4px 8px",
          borderTopRightRadius: 8,
          borderBottomLeftRadius: 8,
          fontSize: 12,
          fontWeight: 500,
        }}
      >
        最佳选择
      </div>
    )}
    <Radio value={type} disabled={disabled} style={{ marginBottom: 12 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <i className={icon} style={{ color: color, fontSize: 18 }} />
        <span style={{ fontWeight: 500, fontSize: 15 }}>{title}</span>
      </div>
    </Radio>

    <div style={{ marginTop: 12, paddingLeft: 22 }}>
      <div
        style={{
          background: `${color}10`,
          padding: "12px",
          borderRadius: 6,
          border: `1px solid ${color}30`,
          marginBottom: 12,
        }}
      >
        <Space direction="vertical" style={{ width: "100%" }}>
          {features.map((feature, index) => (
            <Space key={index}>
              <CheckCircleOutlined style={{ color: color }} />
              <Typography.Text>{feature}</Typography.Text>
            </Space>
          ))}
        </Space>
      </div>

      <div
        style={{
          fontSize: 13,
          color: color,
          padding: "8px 12px",
          background: "#f5f5f5",
          borderRadius: 4,
          border: `1px dashed ${color}50`,
          display: "flex",
          alignItems: "center",
          gap: 4,
        }}
      >
        <i className="ri-information-line" />
        {note}
      </div>
    </div>
  </div>
);

// 项目信息卡片
export const ProjectInfoCard = ({
  projectName,
  isPostChat,
  createdAt,
  useCount,
  summaryCount,
  knowledgeCount,
  keyValue,
  keyValueList, // 添加新的属性
}: {
  projectName: string;
  isPostChat: boolean;
  createdAt?: string;
  useCount?: number;
  summaryCount?: number;
  knowledgeCount?: number;
  keyValue?: string;
  keyValueList?: string[]; // 添加新的类型定义
}) => (
  <div
    style={{
      borderRadius: "8px",
      padding: "16px",
      backgroundColor: isPostChat ? "#e6f7ff" : "#f6ffed",
      marginBottom: 24,
      border: `1px solid ${isPostChat ? "#91d5ff" : "#b7eb8f"}`,
      boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
    }}
  >
    <div style={{ display: "flex", alignItems: "center" }}>
      <div
        style={{
          width: 48,
          height: 48,
          borderRadius: "50%",
          backgroundColor: isPostChat ? "#1890ff" : "#52c41a",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          marginRight: 16,
        }}
      >
        <i
          className={isPostChat ? "ri-timer-flash-line" : "ri-token-swap-line"}
          style={{ color: "#fff", fontSize: 24 }}
        />
      </div>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 18, fontWeight: 600, marginBottom: 4 }}>
          {projectName}
          <Tag
            color={isPostChat ? "blue" : "green"}
            style={{ marginLeft: 12, fontSize: 12, padding: "0 8px" }}
          >
            {isPostChat ? "时长计费" : "Token计费"}
          </Tag>
        </div>
        {(createdAt ||
          summaryCount !== undefined ||
          knowledgeCount !== undefined) && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              fontSize: 13,
              color: "#666",
            }}
          >
            {createdAt && (
              <span
                style={{
                  marginRight: 16,
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <i
                  className="ri-calendar-check-line"
                  style={{ marginRight: 4 }}
                />
                创建于: {new Date(createdAt).toLocaleDateString()}
              </span>
            )}
            {(summaryCount !== undefined || knowledgeCount !== undefined) && (
              <span style={{ display: "flex", alignItems: "center" }}>
                <i className="ri-database-2-line" style={{ marginRight: 4 }} />
                知识库内容: {summaryCount || 0} 摘要 / {knowledgeCount || 0}{" "}
                知识
              </span>
            )}
          </div>
        )}
      </div>
    </div>

    {keyValue && (
      <div
        style={{
          marginTop: 16,
          background: "rgba(255, 255, 255, 0.8)",
          padding: "12px 16px",
          borderRadius: "6px",
          border: "1px solid rgba(0,0,0,0.06)",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: 8,
          }}
        >
          <div style={{ fontSize: 14, fontWeight: 500, color: "#333" }}>
            <i
              className="ri-key-line"
              style={{ marginRight: 6, color: "#722ed1" }}
            />
            项目KEY
            <Tooltip title="在早期此字段也被称作为tianliGPT_key 和 账户KEY，可以公开">
              <i
                className="ri-question-line"
                style={{
                  marginLeft: 4,
                  color: "#8c8c8c",
                  cursor: "help",
                  fontSize: 14,
                }}
              />
            </Tooltip>
          </div>
          <Text
            copyable={{
              text: keyValue,
              tooltips: ["复制API密钥", "已复制!"],
              icon: [
                <span style={{ color: "#1890ff", cursor: "pointer" }}>
                  <i className="ri-file-copy-line" style={{ marginRight: 4 }} />
                  复制
                </span>,
                <span style={{ color: "#52c41a", cursor: "pointer" }}>
                  <i className="ri-check-line" style={{ marginRight: 4 }} />
                  已复制
                </span>,
              ],
            }}
          />
        </div>
        <div
          style={{
            background: "#fafafa",
            padding: "8px 12px",
            borderRadius: "4px",
            fontFamily: "monospace",
            fontSize: 13,
            color: "#333",
            wordBreak: "break-all",
            border: "1px dashed #d9d9d9",
          }}
        >
          {keyValue}
        </div>

        {/* 添加对keyValueList的判断和展示 */}
        {keyValueList && keyValueList.length > 0 && (
          <div
            style={{
              marginTop: 12,
              borderRadius: "4px",
              background: "#fff7e6",
              border: "1px solid #ffe58f",
              padding: "12px",
            }}
          >
            <div
              style={{ display: "flex", alignItems: "center", marginBottom: 8 }}
            >
              <i
                className="ri-information-line"
                style={{ color: "#fa8c16", marginRight: 8, fontSize: 16 }}
              />
              <Text strong style={{ color: "#d46b08" }}>
                此项目存在从老系统导入的KEY
              </Text>
            </div>
            <Text
              type="secondary"
              style={{ fontSize: 13, display: "block", marginBottom: 8 }}
            >
              以下KEY值从老系统导入，所有这些KEY都可以像当前项目KEY一样使用，等同于tianliGPT_key
              或 账户KEY。
            </Text>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {keyValueList.map((key, index) => (
                <div
                  key={index}
                  style={{
                    background: "#fffbe6",
                    padding: "6px 10px",
                    borderRadius: "4px",
                    border: "1px dashed #ffe58f",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <code style={{ fontFamily: "monospace", color: "#d4b106" }}>
                    {key}
                  </code>
                  <Text
                    copyable={{
                      text: key,
                      tooltips: ["复制旧KEY", "已复制!"],
                    }}
                  />
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    )}
  </div>
);
